import { v4 as uuidv4 } from 'uuid';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FormControl, Select, MenuItem, TextField, Chip } from '@mui/material';

import constants from '../../constants';
import { getProperties, setFilter, setFilters, applyFilters } from '../../store/Objects';
import { getOperatorsByType, getPropertyIcon, buildMongoQuery } from '../../utils/filterConfig';

const AdvancedFilterModal = ({ objectType, isOpen, onClose, onGetData, isolate = false, initialFilters = null, customFields = null }) => {
  const objectSchema = constants.getObjectSchema(objectType);

  const dispatch = useDispatch();
  const { properties, filters, filter, activeFilters } = useSelector(state => state['Objects']?.[objectType] || {});

  // Local states
  let [searchProperty, setSearchProperty] = useState('');

  // Filter editing states
  let [editingGroup, setEditingGroup] = useState(null);
  let [localFilter, setLocalFilter] = useState(filter || constants['initialAdvanceFilter']);
  let [localFilters, setLocalFilters] = useState(filters || { filterGroups: [], groupOperator: 'AND' });

  // Track previous isOpen state to detect when modal opens
  const prevIsOpenRef = useRef(false);

  // Sync local state when modal first opens, or when filters are changed externally (chip removal / clear all)
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    // Detect external filter change: modal is open, not isolate, and Redux filters differ from local state
    const externalChange = !isolate && isOpen && !justOpened &&
      JSON.stringify(filters?.filterGroups) !== JSON.stringify(localFilters?.filterGroups);

    if (justOpened || externalChange) {
      const filtersToUse = isolate ? (initialFilters || { filterGroups: [], groupOperator: 'AND' }) : filters;
      const filterToUse = isolate ? constants['initialAdvanceFilter'] : filter;

      setLocalFilter({ ...filterToUse });
      setLocalFilters({ filterGroups: filtersToUse?.filterGroups || [], groupOperator: filtersToUse?.groupOperator || 'AND' });
      if (!filtersToUse?.filterGroups || filtersToUse.filterGroups.length === 0) {
        setEditingGroup(null);
      } else {
        setEditingGroup(filtersToUse.filterGroups[0]);
      }
    }
  }, [isOpen, filter, filters, isolate, initialFilters]);

  useEffect(() => { if (objectType && isOpen == true) dispatch(getProperties()); }, [objectType, isOpen]);

  // Close on Escape — the reactstrap Modal uses backdrop={false} so its built-in keyboard handling doesn't apply.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e['key'] === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Get all properties (system + custom) for the objectType
  // If customFields is provided, use it directly (bypasses Redux/property API)
  const allProperties = Object.entries(properties || {}).flatMap(([group, props]) => Array.isArray(props) ? props.map(prop => ({ ...prop, group, options: prop.options || prop.fieldConfig?.options })) : []);

  // Filter properties based on search and showInFilter setting
  const filteredProperties = allProperties.filter(prop => prop['label']?.toLowerCase().includes(searchProperty?.toLowerCase()) && prop['displayConfig']?.['showInFilter'] !== false);

  // Group filtered properties by their group name
  const groupedProperties = filteredProperties.reduce((acc, prop) => {
    const groupName = prop['group'] || 'Custom Properties';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(prop);
    return acc;
  }, {});

  // Handle property selection
  const handlePropertySelect = (property) => {
    let operators = getOperatorsByType(property['type']);
    let newFilter = { ...constants['initialAdvanceFilter'], property, operator: operators[0], groupId: editingGroup?.['id'] || '', id: uuidv4() };
    setLocalFilter(newFilter);
    if (editingGroup) {
      setLocalFilters({ ...localFilters, filterGroups: localFilters['filterGroups'].map(group => group['id'] === editingGroup['id'] ? { ...group, filters: [...group['filters'], newFilter] } : group) });
      return;
    }

    // Create new filter group if none exists
    const newGroup = { id: uuidv4(), operator: 'AND', filters: [newFilter] };
    setLocalFilter({ ...newFilter, groupId: newGroup['id'] });

    setEditingGroup(newGroup);
    setLocalFilters({ ...localFilters, filterGroups: [...localFilters['filterGroups'], newGroup] });
  }

  // Get operators for selected property (with safety check)
  const availableOperators = localFilter?.property?.type ? getOperatorsByType(localFilter.property.type) : [];

  // Handle operator selection
  const handleOperatorSelect = (value) => {
    const operator = availableOperators.find(op => op['value'] === value);
    const updatedFilter = { ...localFilter, operator, value: '' };
    setLocalFilter(updatedFilter);

    // If operator doesn't require a value, apply the filter immediately
    if (operator && !operator['requiresValue']) {
      handleApplyFilters(updatedFilter, 'default');
    }
  }

  // Delete filter
  const handleDeleteFilter = (groupId, filterId) => {
    const updatedGroups = localFilters['filterGroups'].map(group => {
      if (group['id'] === groupId) return { ...group, filters: group['filters'].filter(f => f['id'] !== filterId) };
      return group;
    }).filter(group => group['filters'].length > 0);

    localFilters = { ...localFilters, filterGroups: updatedGroups };
    setLocalFilters(localFilters);
    setLocalFilter(constants['initialAdvanceFilter']);

    // Skip Redux if isolate mode
    if (!isolate) {
      dispatch(setFilters({ objectType, filters: localFilters }));
      dispatch(setFilter({ objectType, filter: constants['initialAdvanceFilter'] }));
    }

    // setLocalFilters(localFilters);
    // setLocalFilter(constants['initialAdvanceFilter']);

    if (groupId === editingGroup?.['id']) setEditingGroup(null);

    // Refetch data
    handleApplyFilters(constants['initialAdvanceFilter'], 'default');
  }

  // Delete filter group
  const handleDeleteGroup = (groupId) => {
    const updatedGroups = localFilters['filterGroups'].filter(g => g['id'] !== groupId);

    localFilters = { ...localFilters, filterGroups: updatedGroups };

    dispatch(setFilters({ objectType, filters: localFilters }));
    dispatch(setFilter({ objectType, filter: constants['initialAdvanceFilter'] }));

    // setLocalFilters(localFilters);
    // setLocalFilter(constants['initialAdvanceFilter']);

    if (groupId === editingGroup?.['id']) setEditingGroup(null);

    // Refetch data
    handleApplyFilters(constants['initialAdvanceFilter'], 'default');
  }

  // Add new filter to existing group
  const handleAddFilter = (groupId) => {
    setEditingGroup(localFilters['filterGroups'].find(g => g['id'] === groupId));
    setLocalFilter(constants['initialAdvanceFilter']);
    if (!isolate) dispatch(setFilter({ objectType, filter: constants['initialAdvanceFilter'] }));
  }

  // Add new filter group
  const handleAddFilterGroup = () => {
    const newGroup = { id: uuidv4(), operator: 'AND', filters: [] };
    setEditingGroup(newGroup);
    setLocalFilters({ ...localFilters, filterGroups: [...localFilters['filterGroups'], newGroup] });
  }

  // Apply filters
  const handleApplyFilters = async (filterProp, type) => {
    if (type == 'multiValue') localFilter = { ...filterProp, value: { ...filterProp['value'], [filterProp['tempValue']]: true }, tempValue: '' };
    else localFilter = { ...filterProp };
    await setLocalFilter(localFilter);

    let updatedGroups = updateLocalFilters(localFilter);
    localFilters = { ...localFilters, filterGroups: updatedGroups };
    await setLocalFilters(localFilters);

    const query = buildMongoQuery(localFilters['filterGroups'], localFilters['groupOperator']);

    // Save filter state using provided actions (skip Redux if isolate mode)
    if (!isolate) {
      dispatch(setFilter({ objectType, filter: localFilter }));
      dispatch(setFilters({ objectType, filters: localFilters }));
      dispatch(applyFilters({ objectType, filters: query }));
    }

    // Dispatch API call based on view type (also pass filterDefinition in isolate mode)
    onGetData({ objectType, filters: query, filterDefinition: localFilters });
  }

  const handleSetFilter = (filterProp) => {
    setLocalFilter({ ...filterProp });
    if (!isolate) {
      dispatch(setFilter({ objectType, filter: filterProp }));
    }
    setEditingGroup(localFilters['filterGroups'].find(g => g['id'] === filterProp['groupId']));
  }

  // Format filter display
  const formatFilterDisplay = (filterProp) => {
    let valueDisplay = '';
    let { operator } = filterProp;
    if (!operator['requiresValue']) return `${operator['label'] || operator?.['value']}`;

    if (operator['booleanValue'] || filterProp['property']?.['type'] === 'boolean') {
      const b = filterProp['boolean'];
      valueDisplay = b === true || b === 'true' ? 'True' : b === false || b === 'false' ? 'False' : '';
    } else if (filterProp['value'] && Array.isArray(filterProp['value'])) valueDisplay = filterProp['value'].join(', ');
    else if (filterProp['value'] && typeof filterProp['value'] === 'object') {
      if (filterProp['value']['min'] !== undefined && filterProp['value']['max'] !== undefined) valueDisplay = `${filterProp['value']['min']} and ${filterProp['value']['max']}`;
      else if (filterProp['value']['amount'] !== undefined && filterProp['value']['unit'] !== undefined) valueDisplay = `${filterProp['value']['amount']} ${filterProp['value']['unit']}`;
      else valueDisplay = Object.keys(filterProp['value']).filter(key => filterProp['value'][key]).join(', ');
    } else valueDisplay = filterProp['value'];

    return `${operator['label'] || operator?.['value']} ${valueDisplay}`.trim();
  }

  const updateLocalFilters = (localFilter) => {
    let updatedGroups = localFilters['filterGroups'].map(group => {
      if (group['id'] === localFilter['groupId']) return { ...group, filters: group['filters'].map(f => f['id'] === localFilter['id'] ? localFilter : f) };
      return group;
    });

    return updatedGroups;
  };

  // Update localFilters when localFilter changes
  useEffect(() => {
    if (!localFilter['id'] || !localFilter['groupId']) return;

    setLocalFilters(prevFilters => {
      const updatedGroups = prevFilters['filterGroups'].map(group => {
        if (group['id'] === localFilter['groupId']) {
          return {
            ...group,
            filters: group['filters'].map(f => f['id'] === localFilter['id'] ? localFilter : f)
          };
        }
        return group;
      });
      return { ...prevFilters, filterGroups: updatedGroups };
    });
  }, [localFilter['value'], localFilter['operator'], localFilter['id'], localFilter['groupId']]);

  // Render value input based on operator
  const renderValueInput = (filterProp) => {
    let { property, operator } = filterProp;
    if (!operator || !operator['requiresValue']) return null;

    // If property has predefined options (e.g. campaign status), show a select dropdown instead of freetext chips
    if (operator['multiValue'] && property['options']?.length) return (
      <div className='add-value-group'>
        <Select
          fullWidth
          size='small'
          variant='standard'
          value={''}
          displayEmpty
          disableUnderline
          renderValue={() => 'Select value'}
          onChange={(e) => {
            if (!e['target']['value']) return;
            const updated = { ...filterProp, value: { ...(filterProp['value'] || {}), [e['target']['value']]: true } };
            setLocalFilter(updated);
            handleApplyFilters(updated, 'multiValue');
          }}
        >
          {property['options'].filter(opt => !filterProp['value']?.[opt['value']]).map(opt => (
            <MenuItem key={opt['value']} value={opt['value']}>{opt['label']}</MenuItem>
          ))}
        </Select>
        {typeof filterProp['value'] === 'object' && Object.keys(filterProp['value']).some(k => filterProp['value'][k]) && (
          <div className='filter-chips-row'>
            {Object.entries(filterProp['value']).map(([val], index) => {
              if (!val) return null;
              const opt = property['options'].find(o => o['value'] === val);
              return (
                <Chip key={index} label={opt?.['label'] || val} size='small' onDelete={() => {
                  const updated = { ...filterProp, value: { ...filterProp['value'] } };
                  delete updated['value'][val];
                  setLocalFilter(updated);
                  handleApplyFilters(updated, 'multiValue');
                }} />
              );
            })}
          </div>
        )}
      </div>
    );

    if (operator['multiValue']) return (
      <>
        <div className='add-value-group'>
          <TextField
            fullWidth
            hiddenLabel
            size='small'
            variant='standard'
            placeholder='Add values'
            value={filterProp['tempValue']}
            onChange={(e) => setLocalFilter({ ...filterProp, tempValue: e['target']['value'] })}
            onKeyDown={(e) => e['key'] === 'Enter' && handleApplyFilters(filterProp, 'multiValue')}
          />
        </div>

        <div style={{ marginTop: 8 }}>
          {typeof filterProp['value'] === 'object' && Object.entries(filterProp['value']).map(([val, _], index) => {
            if (!val) return null;
            return (
              <Chip key={index} label={val} size='small' style={{ margin: 2 }} onDelete={() => {
                delete filterProp['value'][val];
                setLocalFilter({ ...filterProp });
                handleApplyFilters(filterProp, 'multiValue');
              }} />
            )
          })}
        </div>
        {/* <div className='more-filter'> <button className='btn-style-border' onClick={() => handleApplyFilters(filterProp, 'multiValue')}>Apply</button> </div> */}
      </>
    )

    if (operator['isBetween']) return (
      <div className='add-value-group' style={{ display: 'flex', gap: 8 }}>
        <TextField
          hiddenLabel
          size='small'
          variant='standard'
          placeholder='Min'
          value={filterProp['value']['min']}
          type={property['type'] === 'date' || property['type'] === 'datetime' ? 'date' : 'number'}
          onKeyDown={(e) => e['key'] === 'Enter' && handleApplyFilters(filterProp, 'isBetween')}
          onChange={(e) => {
            const newFilter = { ...filterProp, value: { ...filterProp['value'], min: e['target']['value'] } };
            setLocalFilter(newFilter);
            // Auto-apply when both values are filled
            if (e['target']['value'] && filterProp['value']['max']) handleApplyFilters(newFilter, 'isBetween');
          }}
        />
        <TextField
          hiddenLabel
          size='small'
          variant='standard'
          placeholder='Max'
          value={filterProp['value']['max']}
          type={property['type'] === 'date' || property['type'] === 'datetime' ? 'date' : 'number'}
          onKeyDown={(e) => e['key'] === 'Enter' && handleApplyFilters(filterProp, 'isBetween')}
          onChange={(e) => {
            const newFilter = { ...filterProp, value: { ...filterProp['value'], max: e['target']['value'] } };
            setLocalFilter(newFilter);
            // Auto-apply when both values are filled
            if (filterProp['value']['min'] && e['target']['value']) handleApplyFilters(newFilter, 'isBetween');
          }}
        />
      </div>
    )

    if (operator['isRelative']) return (
      <div className='add-value-group' style={{ display: 'flex', gap: 8 }}>
        <TextField
          hiddenLabel
          size='small'
          type='number'
          variant='standard'
          placeholder='Amount'
          style={{ width: 80 }}
          value={filterProp['value']['amount']}
          onKeyDown={(e) => e['key'] === 'Enter' && handleApplyFilters(filterProp, 'isRelative')}
          onChange={(e) => setLocalFilter({ ...filterProp, value: { ...filterProp['value'], amount: parseInt(e['target']['value']) || 1 } })}
        />
        <Select
          size='small'
          variant='standard'
          value={filterProp['value']['unit']}
          onKeyDown={(e) => e['key'] === 'Enter' && handleApplyFilters(filterProp, 'isRelative')}
          onChange={(e) => setLocalFilter({ ...filterProp, value: { ...filterProp['value'], unit: e['target']['value'] } })}
        >
          <MenuItem value='days'>days</MenuItem>
          <MenuItem value='weeks'>weeks</MenuItem>
          <MenuItem value='months'>months</MenuItem>
          <MenuItem value='years'>years</MenuItem>
        </Select>
      </div>
    )

    if (operator['booleanValue'] || property['type'] === 'boolean') {
      const boolStrVal = filterProp['boolean'] === true || filterProp['boolean'] === 'true' ? 'true' : filterProp['boolean'] === false || filterProp['boolean'] === 'false' ? 'false' : '';
      return (
        <div className='add-value-group'>
          <Select
            fullWidth
            size='small'
            variant='standard'
            displayEmpty
            value={boolStrVal}
            onChange={(e) => { if (e['target']['value'] === '') return; const updated = { ...filterProp, boolean: e['target']['value'] === 'true' }; setLocalFilter(updated); handleApplyFilters(updated, 'booleanValue'); }}
            renderValue={(selected) => { if (selected === '') return <span style={{ color: '#9CA3AF' }}>Select an option</span>; return selected === 'true' ? 'True' : 'False'; }}
          >
            <MenuItem value='' disabled>Select an option</MenuItem>
            <MenuItem value='true'>True</MenuItem>
            <MenuItem value='false'>False</MenuItem>
          </Select>
        </div>
      );
    }

    // Select input — show dropdown with property options
    if (property['type'] === 'select' && property['options']?.length) {
      return (
        <div className='add-value-group'>
          <Select
            fullWidth
            size='small'
            variant='standard'
            value={filterProp['value'] || ''}
            displayEmpty
            onChange={(e) => { const updated = { ...filterProp, value: e['target']['value'] }; setLocalFilter(updated); handleApplyFilters(updated, 'default'); }}
          >
            <MenuItem value='' disabled>Select value</MenuItem>
            {property['options'].map(opt => (
              <MenuItem key={opt['value']} value={opt['value']}>{opt['label']}</MenuItem>
            ))}
          </Select>
        </div>
      );
    }

    // Date/datetime input - show calendar picker
    if (property['type'] === 'date' || property['type'] === 'datetime') {
      return (
        <div className='add-value-group'>
          <TextField fullWidth hiddenLabel size='small' variant='standard' type='date' value={filterProp['value']} onChange={(e) => { setLocalFilter({ ...filterProp, value: e['target']['value'] }); handleApplyFilters({ ...filterProp, value: e['target']['value'] }, 'default'); }} />
        </div>
      )
    }

    // Default text/number input
    return (
      <div className='add-value-group'>
        <TextField
          fullWidth
          hiddenLabel
          size='small'
          variant='standard'
          placeholder='Enter value'
          value={filterProp['value']}
          onChange={(e) => setLocalFilter({ ...filterProp, value: e['target']['value'] })}
          onKeyDown={(e) => e['key'] === 'Enter' && handleApplyFilters(filterProp, 'default')}
          type={property['type'] === 'number' || property['type'] === 'currency' || property['type'] === 'percentage' ? 'number' : 'text'}
        />
      </div>
    )
  }

  const showAllProperties = () => {
    // If properties are not loaded yet, show loading message
    // For object types with objectSchema (deals, contacts, companies), check objectSchema
    // For object types without objectSchema (agents), just check if properties exist
    const hasSchema = objectSchema !== null && objectSchema !== undefined;
    const hasProperties = properties && Object.keys(properties).length > 0;

    if (!hasSchema && !hasProperties) {
      return (
        <>
          <h4>Add filter</h4>
          <div className='filters-area'>
            <p>Loading properties...</p>
          </div>
        </>
      );
    }

    return (
      <>
        <h4>Add filter</h4>
        <div className='searchbox'>
          <i className='icon'>
            <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <g clipPath='url(#clip0_224_579)'>
                <path d='M17.8568 16.2345L13.203 11.5807C14.0782 10.377 14.5958 8.8965 14.5958 7.2975C14.5958 3.27375 11.322 0 7.2975 0C3.27375 0 0 3.27375 0 7.2975C0 11.322 3.27375 14.595 7.2975 14.595C8.823 14.595 10.2397 14.1247 11.4128 13.3215L16.0912 18L17.8568 16.2345ZM2.1405 7.2975C2.1405 4.4535 4.45425 2.13975 7.29825 2.13975C10.1423 2.13975 12.456 4.4535 12.456 7.2975C12.456 10.1415 10.1423 12.4552 7.29825 12.4552C4.4535 12.4552 2.1405 10.1415 2.1405 7.2975Z' fill='#89868D' />
              </g>
              <defs>
                <clipPath id='clip0_224_579'>
                  <rect width='18' height='18' fill='white' />
                </clipPath>
              </defs>
            </svg>
          </i>
          <TextField
            fullWidth
            hiddenLabel
            size='small'
            variant='standard'
            value={searchProperty}
            placeholder={`Search in ${objectSchema?.['plural'] || objectType} properties...`}
            onChange={(e) => setSearchProperty(e['target']['value'])}
          />
        </div>
        <div className='deal-activity'>
          <h4>{objectSchema?.['label'] || objectType.charAt(0).toUpperCase() + objectType.slice(1)} Properties</h4>
          {Object.entries(groupedProperties).map(([groupName, properties]) => (
            <div key={groupName}>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}> {groupName}</p>
              <div className='deal-filters'>
                {properties.map((prop) => {
                  let propIcon = getPropertyIcon(prop['type']);
                  return (
                    <button key={prop['name']} className='filter-btn' onClick={() => handlePropertySelect(prop)}>
                      <i className={`icon ${propIcon['icon']}`}> {propIcon['text']} </i>
                      {prop['label']}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <Modal isOpen={isOpen} modalClassName='right right-without-bg' backdrop={false} className={`main-modal right right-side-modal style-three`}>
      <ModalHeader toggle={onClose}>
        <h3>All Filters</h3>
      </ModalHeader>
      <ModalBody>
        <div className='filters-modal-content'>
          <div className='function-box'>
            <div className='accordioan-box'>
              {localFilters['filterGroups'].length > 0 || localFilter['property']
                ? <div className='filters-box'>
                  <div className='left-all-filters-area'>
                    <h4>
                      Advanced Filters
                      {/* <button className='btn-style-border' onClick={handleClearFilters}>Clear</button> */}
                    </h4>
                    <div className='filters-area'>
                      {localFilters['filterGroups'].map((group, groupIndex) => (
                        <div key={group['id']}>
                          <div className='title-area'>
                            <h4>Group {groupIndex + 1}</h4>
                            <div className='right-copy-area'>
                              <button className='copy-btn-area' onClick={() => handleDeleteGroup(group['id'])}>
                                <i className='fa fa-trash'></i>
                              </button>
                            </div>
                          </div>
                          {group['filters'].map((filter) => (
                            <div key={filter['id']} className='group-box' onClick={() => handleSetFilter(filter)}>
                              <p> <b>{filter['property']['label']} </b> {formatFilterDisplay(filter)} </p>
                              <button className='copy-btn-area' onClick={() => handleDeleteFilter(filter['groupId'], filter['id'])}><i className='fa fa-trash'></i></button>
                            </div>
                          ))}
                          <h4>{group['operator']}</h4>
                          <div className='add-filters'> <button className='btn-style-border' onClick={() => handleAddFilter(group['id'])}>+ Add filter</button> </div>
                          {groupIndex < localFilters['filterGroups'].length - 1 && <div className='more-filter'> <h4>{localFilters['groupOperator']}</h4> </div>}
                        </div>
                      ))}
                      <div className='more-filter'>
                        <h4 className='text-center'>OR</h4>
                        <button className='btn-style-border' onClick={handleAddFilterGroup}>+ Add filter group</button>
                      </div>
                    </div>
                  </div>
                  <div className='right-filters-area'>
                    {localFilter['property'] ? (
                      <>
                        <h4>Edit filter</h4>
                        <div className='filters-area-edit'>
                          <div className='title-area'>
                            <h4>{localFilter['property']['label']}</h4>
                            <div className='right-copy-area'>
                              <button className='copy-btn-area' onClick={() => handleDeleteFilter(localFilter['groupId'], localFilter['id'])}><i className='fa fa-exchange-alt'></i></button>
                            </div>
                          </div>
                          <div className='group-box'>
                            <FormControl fullWidth>
                              <Select
                                displayEmpty
                                value={localFilter['operator']?.value || ''}
                                onChange={(e) => handleOperatorSelect(e['target']['value'])}
                                renderValue={(selected) => {
                                  if (!selected) return 'Select operator';
                                  const op = availableOperators.find(o => o['value'] === selected);
                                  return op?.['label'] || selected;
                                }}
                              >
                                {availableOperators.map((op) => <MenuItem key={op['value']} value={op['value']}>{op['label']}</MenuItem>)}
                              </Select>
                            </FormControl>
                            {renderValueInput(localFilter)}
                          </div>
                        </div>
                      </>
                    ) : showAllProperties()
                    }
                  </div>
                </div>
                : <div className='filters-box'>
                  <div className='left-all-filters-area'>
                    <h4>Advanced Filters</h4>
                    <div className='filters-area'>
                      <p>This view doesn't have any advanced filters. Select a filter to begin.</p>
                    </div>
                  </div>
                  <div className='right-filters-area'>
                    {showAllProperties()}
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AdvancedFilterModal;